import React, {
	// eslint-disable-next-line jira/restricted/react-component-props
	type ComponentProps,
	type ComponentType,
	type SyntheticEvent,
	type KeyboardEvent,
	useState,
} from 'react';
import { styled } from '@compiled/react';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import {
	Draggable,
	type DraggableProvided,
	type DraggableStateSnapshot,
} from 'react-beautiful-dnd';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import Icon from '@atlaskit/icon';
import EditorCloseIcon from '@atlaskit/icon/core/migration/cross--editor-close';
import ErrorIcon from '@atlaskit/icon/core/migration/error';
import TextField from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import DisabledButtonWithTooltip from '@atlassian/jira-disabled-button-with-tooltip/src/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { type IntlShape, useIntl } from '@atlassian/jira-intl';
import { ColorIndicator } from '@atlassian/jira-option-color-picker/src/color-indicator/index.tsx';
import { toDraggableId } from '@atlassian/jira-project-settings-apps-common/src/options/index.tsx';
import type { AssociatedField, OptionType } from '../../../../../types/layout-items.tsx';
import { OptionColorPicker } from './color-picker/index.tsx';
import messages from './messages.tsx';

const DragHandle = (props: ComponentProps<'svg'>) => (
	<svg {...props} width="24px" height="24px" viewBox="0 0 24 24">
		<g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
			<mask fill={token('elevation.surface.raised')}>
				<path d="M10.3333333,18 C9.59695367,18 9,17.4030463 9,16.6666667 C9,15.930287 9.59695367,15.3333333 10.3333333,15.3333333 C11.069713,15.3333333 11.6666667,15.930287 11.6666667,16.6666667 C11.6666667,17.4030463 11.069713,18 10.3333333,18 Z M10.3333333,13.3333333 C9.59695367,13.3333333 9,12.7363797 9,12 C9,11.2636203 9.59695367,10.6666667 10.3333333,10.6666667 C11.069713,10.6666667 11.6666667,11.2636203 11.6666667,12 C11.6666667,12.7363797 11.069713,13.3333333 10.3333333,13.3333333 Z M10.3333333,8.66666667 C9.59695367,8.66666667 9,8.069713 9,7.33333333 C9,6.59695367 9.59695367,6 10.3333333,6 C11.069713,6 11.6666667,6.59695367 11.6666667,7.33333333 C11.6666667,8.069713 11.069713,8.66666667 10.3333333,8.66666667 Z M14.3333333,18 C13.5969537,18 13,17.4030463 13,16.6666667 C13,15.930287 13.5969537,15.3333333 14.3333333,15.3333333 C15.069713,15.3333333 15.6666667,15.930287 15.6666667,16.6666667 C15.6666667,17.4030463 15.069713,18 14.3333333,18 Z M14.3333333,13.3333333 C13.5969537,13.3333333 13,12.7363797 13,12 C13,11.2636203 13.5969537,10.6666667 14.3333333,10.6666667 C15.069713,10.6666667 15.6666667,11.2636203 15.6666667,12 C15.6666667,12.7363797 15.069713,13.3333333 14.3333333,13.3333333 Z M14.3333333,8.66666667 C13.5969537,8.66666667 13,8.069713 13,7.33333333 C13,6.59695367 13.5969537,6 14.3333333,6 C15.069713,6 15.6666667,6.59695367 15.6666667,7.33333333 C15.6666667,8.069713 15.069713,8.66666667 14.3333333,8.66666667 Z" />
			</mask>
			<path
				d="M10.3333333,18 C9.59695367,18 9,17.4030463 9,16.6666667 C9,15.930287 9.59695367,15.3333333 10.3333333,15.3333333 C11.069713,15.3333333 11.6666667,15.930287 11.6666667,16.6666667 C11.6666667,17.4030463 11.069713,18 10.3333333,18 Z M10.3333333,13.3333333 C9.59695367,13.3333333 9,12.7363797 9,12 C9,11.2636203 9.59695367,10.6666667 10.3333333,10.6666667 C11.069713,10.6666667 11.6666667,11.2636203 11.6666667,12 C11.6666667,12.7363797 11.069713,13.3333333 10.3333333,13.3333333 Z M10.3333333,8.66666667 C9.59695367,8.66666667 9,8.069713 9,7.33333333 C9,6.59695367 9.59695367,6 10.3333333,6 C11.069713,6 11.6666667,6.59695367 11.6666667,7.33333333 C11.6666667,8.069713 11.069713,8.66666667 10.3333333,8.66666667 Z M14.3333333,18 C13.5969537,18 13,17.4030463 13,16.6666667 C13,15.930287 13.5969537,15.3333333 14.3333333,15.3333333 C15.069713,15.3333333 15.6666667,15.930287 15.6666667,16.6666667 C15.6666667,17.4030463 15.069713,18 14.3333333,18 Z M14.3333333,13.3333333 C13.5969537,13.3333333 13,12.7363797 13,12 C13,11.2636203 13.5969537,10.6666667 14.3333333,10.6666667 C15.069713,10.6666667 15.6666667,11.2636203 15.6666667,12 C15.6666667,12.7363797 15.069713,13.3333333 14.3333333,13.3333333 Z M14.3333333,8.66666667 C13.5969537,8.66666667 13,8.069713 13,7.33333333 C13,6.59695367 13.5969537,6 14.3333333,6 C15.069713,6 15.6666667,6.59695367 15.6666667,7.33333333 C15.6666667,8.069713 15.069713,8.66666667 14.3333333,8.66666667 Z"
				fill={token('color.icon')}
			/>
		</g>
	</svg>
);

export type DefaultValueControlComponentProps = {
	field: AssociatedField;
	isSelected: boolean;
	option: OptionType;
	isOptionHovered: boolean;
	isOptionFocused: boolean;
	onFieldUpdated: (field: AssociatedField) => void;
	tooltipDelay?: number;
	testId?: string;
};

export type Props = {
	readonly DefaultValueControlLeft?: ComponentType<DefaultValueControlComponentProps>;
	readonly DefaultValueControlRight?: ComponentType<DefaultValueControlComponentProps>;
	readonly autoFocus: boolean;
	readonly field: AssociatedField;
	readonly index: number;
	readonly isDefault: boolean;
	/**
	 * This is different from field operations.
	 * So if this property `isDeletable` is false - the X button is gone completely.
	 * If `field.operations.canModifyOptions` is false - the X button is present but shows a tooltip.
	 *
	 * We need this split because the last option cannot be deleted, regardless of field operations.
	 */
	readonly isDeletable: boolean;
	readonly onEdit: (option: OptionType, newName: string) => void;
	readonly onSaveColor: (option: OptionType, newColor: string) => void;
	readonly onEnterKey: () => void;
	readonly onRemove: (option: OptionType) => void;
	readonly onUpdate: (field: AssociatedField) => void;
	readonly option: OptionType;
	readonly placeholder: string;
	tooltipDelay?: number;
	testId?: string;
	/**
	 * Undefined means that this option is valid
	 */
	readonly translatedError: string | undefined;
};

export const Option = (props: Props) => {
	const intl: IntlShape = useIntl();
	/*
	 * Only show validation error when user has touched this field
	 * so that we avoid screaming in red when user has just only created the option.
	 */
	const [touched, setTouched] = useState<boolean>(false);
	const [focused, setFocused] = useState<boolean>(false);
	const [hovered, setHovered] = useState<boolean>(false);
	const [removeEmptyOptionTimeout, setEmptyOptionTimeout] = useState<
		ReturnType<typeof setTimeout> | undefined
	>(undefined);

	const onTouch = () => {
		setTouched(true);
	};

	const onRemove = () => {
		props.onRemove(props.option);
	};

	const onBlur = () => {
		setFocused(false);
		onTouch();
		const isColorfulSingleSelect =
			props.field.type === 'com.atlassian.jira.plugin.system.customfieldtypes:select' &&
			expVal(
				'thor_colourful_single_select_milestone2_experiment',
				'isColorfulSingleSelectEnabled',
				false,
			);
		if (
			props.isDeletable &&
			props.option.value === '' &&
			(!isColorfulSingleSelect || !props.option.colour)
		) {
			setEmptyOptionTimeout(setTimeout(onRemove));
		}
	};

	const onFocus = () => {
		setFocused(true);
		clearTimeout(removeEmptyOptionTimeout);
	};

	const onMouseEnter = () => setHovered(true);

	const onMouseLeave = () => setHovered(false);

	const onChange = (e: SyntheticEvent<HTMLInputElement>) => {
		onTouch();
		props.onEdit(props.option, e.currentTarget.value);
	};

	const onSave = (data: { color: string; name: string }) => {
		onTouch();
		props.onSaveColor(props.option, data.color);
	};

	const onKeyDown = (e: KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			props.onEnterKey();
		}
	};

	const isInvalid = (): boolean => {
		const { option, translatedError } = props;

		// Only show validation error when user has touched this field
		// so that we avoid screaming in red when user has just only created the option.
		// In case the option is already saved on the server and is invalid, make the error display immediately.
		// That prevent the case when collapsed field card shows error icon, but there is no error inside!
		// This should in theory never happen - but it does anyway because we keep adding new validation rules,
		// but never retrospectively check the data.
		return (touched || option.id !== null) && translatedError != null && translatedError.length > 0;
	};

	const renderDefaultValueControl = (
		DefaultValueControlComponent?: ComponentType<DefaultValueControlComponentProps>,
	) => {
		if (DefaultValueControlComponent == null) {
			return null;
		}

		const {
			field,
			field: {
				operations: { canModifyOptions },
			},
			option,
			isDefault,
			onUpdate,
		} = props;

		return (
			<AlignedOptionControl isDisabled={!canModifyOptions}>
				<DefaultValueControlComponent
					option={option}
					field={field}
					isSelected={isDefault}
					isOptionFocused={focused}
					isOptionHovered={hovered}
					onFieldUpdated={onUpdate}
				/>
			</AlignedOptionControl>
		);
	};

	const {
		index,
		option,
		placeholder,
		autoFocus,
		translatedError,
		isDeletable,
		field: {
			type: fieldType,
			operations: { canModifyOptions },
		},
		DefaultValueControlLeft,
		DefaultValueControlRight,
	} = props;

	const renderTextField = () => (
		<Tooltip
			content={canModifyOptions ? null : intl.formatMessage(messages.disabledInputTooltip)}
			delay={props?.tooltipDelay}
		>
			<TextField
				name="select-custom-field-option"
				value={option.value}
				placeholder={placeholder}
				maxLength={255}
				autoFocus={autoFocus}
				onChange={onChange}
				onKeyDown={onKeyDown}
				isDisabled={!canModifyOptions}
				appearance="none"
				testId={props?.testId && `${props?.testId}.text-field.test-id-wrapper`}
			/>
		</Tooltip>
	);

	return (
		<Draggable
			draggableId={toDraggableId(option.optimisticId)}
			index={index}
			isDragDisabled={!canModifyOptions}
		>
			{(dragProvided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
				<Margin
					ref={dragProvided.innerRef}
					{...dragProvided.draggableProps}
					onFocus={onFocus}
					onBlur={onBlur}
					onMouseEnter={onMouseEnter}
					onMouseLeave={onMouseLeave}
				>
					<Item invalid={isInvalid()} isDragging={snapshot.isDragging}>
						<Tooltip
							content={
								canModifyOptions ? null : intl.formatMessage(messages.disabledSortHandleTooltip)
							}
							delay={props?.tooltipDelay}
						>
							<AlignedOptionControl
								{...dragProvided.dragHandleProps}
								isDisabled={!canModifyOptions}
								tabIndex={!canModifyOptions ? -1 : 0}
								data-testid={props?.testId && `${props?.testId}.default.test-id-wrapper`}
								role="button"
								aria-roledescription={intl.formatMessage(messages.draggableItemRole)}
							>
								<Icon glyph={DragHandle} label="" />
							</AlignedOptionControl>
						</Tooltip>

						<OptionControlGroup>
							<Row>
								{renderDefaultValueControl(DefaultValueControlLeft)}

								{expVal(
									'thor_colourful_single_select_milestone2_experiment',
									'isColorfulSingleSelectEnabled',
									false,
								) && (
									<AlignedOptionControl isDisabled={!canModifyOptions}>
										<ColorIndicator
											canModifyOptions={canModifyOptions}
											fieldType={fieldType}
											color={option.colour}
										/>
									</AlignedOptionControl>
								)}

								<InputCentred>{renderTextField()}</InputCentred>

								{renderDefaultValueControl(DefaultValueControlRight)}
								{expVal(
									'thor_colourful_single_select_milestone2_experiment',
									'isColorfulSingleSelectEnabled',
									false,
								) && (
									<AlignedOptionControl isDisabled={!canModifyOptions}>
										<OptionColorPicker
											canModifyOptions={canModifyOptions}
											fieldType={fieldType}
											option={option}
											onSave={onSave}
										/>
									</AlignedOptionControl>
								)}
								{isDeletable ? (
									<AlignedOptionControl isDisabled={!canModifyOptions}>
										<Tooltip
											content={intl.formatMessage(
												canModifyOptions
													? messages.removeOption
													: messages.disabledRemoveButtonTooltip,
											)}
											delay={props?.tooltipDelay}
										>
											<DisabledButtonWithTooltip
												isDisabled={!canModifyOptions}
												data-testid={props?.testId && `${props?.testId}.button.test-id-wrapper`}
											>
												<Button
													shouldFitContainer
													appearance="subtle"
													// This does not need a11y message because there is aria-label on the button element
													iconBefore={<EditorCloseIcon spacing="spacious" label="" />}
													aria-label={intl.formatMessage(messages.removeOption)}
													onClick={onRemove}
													isDisabled={!canModifyOptions}
												/>
											</DisabledButtonWithTooltip>
										</Tooltip>
									</AlignedOptionControl>
								) : null}
							</Row>
							{isInvalid() ? (
								<Heading size="xxsmall" as="div">
									<ErrorMessage>
										{/* The icon has no label because there is the error message right next to it */}
										<ErrorIcon
											color={token('color.text.accent.orange.bolder')}
											LEGACY_size="small"
											label=""
										/>
										<span>{translatedError}</span>
									</ErrorMessage>
								</Heading>
							) : null}
						</OptionControlGroup>
					</Item>
				</Margin>
			)}
		</Draggable>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InputCentred = styled.div({
	flex: 1,
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > div': {
		width: '100%',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Margin = styled.li({
	marginBottom: token('space.050'),
	listStyleType: 'none',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Item = styled.div<{
	invalid: boolean;
	isDragging: boolean;
}>(
	{
		borderWidth: '2px',
		borderStyle: 'solid',
		borderRadius: '3px',
		listStyleType: 'none',
		paddingTop: 0,
		paddingRight: token('space.050'),
		paddingBottom: 0,
		paddingLeft: token('space.050'),
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		borderColor: (props) =>
			props.invalid ? token('color.border.danger') : token('color.border.input'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		boxShadow: (props) =>
			props.invalid
				? `0 0 0 1px ${token('color.border.danger')}`
				: `1px 1px 2px -1px ${token('color.border.input')}`,

		backgroundColor: token('color.background.input'),
		'&:hover': {
			backgroundColor: token('color.background.input.hovered'),
		},
	},
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		zIndex: (props) => (props.isDragging ? 300 : 'auto'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		boxShadow: (props) =>
			props.isDragging
				? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`0 ${gridSize}px ${gridSize * 2}px ${-gridSize / 2}px ${token('elevation.shadow.raised')}, 0 0 1px ${token('elevation.shadow.raised')};`
				: '',
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Row = styled.div({
	display: 'flex',
	flexDirection: 'row',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Input = styled.div({
	flex: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const AlignedOptionControl = styled.div<{
	isDisabled: boolean;
}>({
	/* The option input is 40px so we set a matching height to ensure all controls are vertically aligned */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 5}px`,
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	cursor: (props) => (props.isDisabled ? 'not-allowed' : undefined),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const OptionControlGroup = styled.div({
	display: 'flex',
	flex: 1,
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ErrorMessage = styled.div({
	display: 'flex',

	color: token('color.text.danger'),
	width: '100%',
	marginTop: 0,
	marginRight: token('space.050'),
	marginBottom: token('space.100'),
	marginLeft: token('space.100'),
	fontWeight: token('font.weight.regular'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const DefaultValueLabel = styled.label<{
	isDisabled: boolean;
}>({
	color: token('color.text'),
	marginRight: token('space.150'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	cursor: (props) => (props.isDisabled ? 'not-allowed' : 'initial'),
});
